const API_BASE = process.env.REACT_APP_API_URL || '/api';

async function request(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  if (res.status === 401 && !path.startsWith('/auth/login')) {
    window.dispatchEvent(new Event('bidboard-session-expired'));
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

export const api = {
  getProjects: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/projects${qs ? `?${qs}` : ''}`);
  },
  getProject: (id) => request(`/projects/${id}`),
  createProject: (data) => request('/projects', { method: 'POST', body: JSON.stringify(data) }),
  updateProject: (id, data) => request(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  // `extra` carries award-required fields (EstStartDate/ProjectCost/DateAwarded)
  // when the award modal collects them alongside the flip to Awarded.
  updateStatus: (id, StatusId, extra = {}) => request(`/projects/${id}/status`, { method: 'PATCH', body: JSON.stringify({ StatusId, ...extra }) }),
  togglePriority: (id, IsPriority) => request(`/projects/${id}/priority`, { method: 'PATCH', body: JSON.stringify({ IsPriority }) }),
  getActivityLog: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/projects/activity-log${qs ? `?${qs}` : ''}`);
  },
  getActivity: (id) => request(`/projects/${id}/activity`),
  addActivity: (id, data) => request(`/projects/${id}/activity`, { method: 'POST', body: JSON.stringify(data) }),
  getRevisions: (id) => request(`/projects/${id}/revisions`),
  createRevision: (id) => request(`/projects/${id}/revisions`, { method: 'POST' }),
  generateRevisionSummary: (id) => request(`/projects/${id}/revision-summary`, { method: 'POST' }),
  deleteProject: (id) => request(`/projects/${id}`, { method: 'DELETE' }),
  archiveProject: (id, IsArchived) => request(`/projects/${id}/archive`, { method: 'PATCH', body: JSON.stringify({ IsArchived }) }),
  bulkArchive: (projectIds, IsArchived) => request('/projects/bulk-archive', { method: 'PATCH', body: JSON.stringify({ projectIds, IsArchived }) }),

  getAiUsage: () => request('/ai-usage'),

  getStatuses: () => request('/lookups/statuses'),
  getProjectTypes: () => request('/lookups/project-types'),
  getOwnerTypes: () => request('/lookups/owner-types'),
  getTeam: () => request('/team'),
  getEstimators: () => request('/team/estimators'),
  getCoordinators: () => request('/team/coordinators'),
  getPEs: () => request('/team/pes'),
  getPMs: () => request('/team/pms'),
  getReviewers: () => request('/team/reviewers'),
  createTeamMember: (data) => request('/team', { method: 'POST', body: JSON.stringify(data) }),

  getFollowups: (projectId) => request(`/projects/${projectId}/followups`),
  addFollowup: (projectId, data) => request(`/projects/${projectId}/followups`, { method: 'POST', body: JSON.stringify(data) }),
  completeFollowup: (id) => request(`/followups/${id}/complete`, { method: 'PATCH' }),
  getUpcomingFollowups: () => request('/followups/upcoming'),

  getClients: () => request('/clients'),
  searchClients: (q) => request(`/clients/search?q=${encodeURIComponent(q)}`),
  updateClient: (id, data) => request(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  createClient: (data) => request('/clients', { method: 'POST', body: JSON.stringify(data) }),

  getBidLevelingScopes: () => request('/bid-leveling/scopes'),
  getProjectBidLeveling: (projectId) => request(`/bid-leveling/${projectId}`),
  initProjectScopes: (projectId, scopeCategoryIds) => request(`/bid-leveling/${projectId}/scopes`, { method: 'POST', body: JSON.stringify({ scopeCategoryIds }) }),
  deleteScope: (projectId, scopeId) => request(`/bid-leveling/${projectId}/scopes/${scopeId}`, { method: 'DELETE' }),
  toggleScopeVisibility: (projectId, scopeId, isVisible) => request(`/bid-leveling/${projectId}/scopes/${scopeId}/visibility`, { method: 'PATCH', body: JSON.stringify({ isVisible }) }),
  addScopeLineItem: (projectId, scopeId, description) => request(`/bid-leveling/${projectId}/scopes/${scopeId}/line-items`, { method: 'POST', body: JSON.stringify({ Description: description }) }),
  addSubBid: (projectId, scopeId, data) => request(`/bid-leveling/${projectId}/scopes/${scopeId}/bids`, { method: 'POST', body: JSON.stringify(data) }),
  updateSubBidField: (bidId, field, value) => request(`/bid-leveling/bids/${bidId}`, { method: 'PATCH', body: JSON.stringify({ field, value }) }),
  toggleRecommendBid: (bidId) => request(`/bid-leveling/bids/${bidId}/recommend`, { method: 'PATCH' }),
  deleteSubBid: (bidId) => request(`/bid-leveling/bids/${bidId}`, { method: 'DELETE' }),
  getProjectBundles: (projectId) => request(`/bid-leveling/${projectId}/bundles`),
  deleteLineItem: (projectId, scopeId, lineItemId) => request(`/bid-leveling/${projectId}/scopes/${scopeId}/line-items/${lineItemId}`, { method: 'DELETE' }),
  reorderLineItems: (projectId, scopeId, lineItemIds) => request(`/bid-leveling/${projectId}/scopes/${scopeId}/line-items/reorder`, { method: 'PATCH', body: JSON.stringify({ lineItemIds }) }),
  findBidPassage: (bidId, scopeItem) => request(`/bid-leveling/bids/${bidId}/find-passage`, { method: 'POST', body: JSON.stringify({ scopeItem }) }),
  reviewBids: (projectId, scopeId) => request(`/bid-leveling/${projectId}/scopes/${scopeId}/review`, { method: 'POST' }),
  rateSub: (bidId, rating, notes) => request(`/bid-leveling/bids/${bidId}/rate`, { method: 'POST', body: JSON.stringify({ rating, notes }) }),
  getSubRatingHistory: (subContractorId) => request(`/bid-leveling/subcontractors/${subContractorId}/rating-history`),
  setBidLock: (bidId, isLocked) => request(`/bid-leveling/bids/${bidId}/lock`, { method: 'PATCH', body: JSON.stringify({ isLocked }) }),

  getGuestbook: () => request('/guestbook'),
  signGuestbook: (message) => request('/guestbook', { method: 'POST', body: JSON.stringify({ message }) }),

  getUnreadNotifications: () => request('/notifications/unread'),
  markNotificationRead: (id) => request(`/notifications/${id}/read`, { method: 'PATCH' }),
  markAllNotificationsRead: () => request('/notifications/read-all', { method: 'PATCH' }),

  getAdminUsers: () => request('/admin/users'),
  createUser: (data) => request('/admin/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  resetUserPassword: (id, data) => request(`/admin/users/${id}/reset-password`, { method: 'POST', body: JSON.stringify(data) }),
  archiveUser: (id, archived) => request(`/admin/users/${id}/archive`, { method: 'PATCH', body: JSON.stringify({ archived }) }),
  getLoginAudit: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/auth/login-audit${qs ? `?${qs}` : ''}`);
  },

  buddyQuip: (buddyName, buddyBio, eventDescription) =>
    request('/buddy/quip', { method: 'POST', body: JSON.stringify({ buddyName, buddyBio, eventDescription }) }),

  getBuddyState: () => request('/buddy/state'),
  saveBuddyState: (state) => request('/buddy/state', { method: 'PUT', body: JSON.stringify(state) }),
  listBuddyUsers: () => request('/buddy/admin/users'),
  setUserBuddy: (username, state) => request(`/buddy/admin/users/${encodeURIComponent(username)}`, { method: 'PUT', body: JSON.stringify(state) }),
  deleteUserBuddy: (username) => request(`/buddy/admin/users/${encodeURIComponent(username)}`, { method: 'DELETE' }),

  snapshotProdToDev: () => request('/admin/snapshot', { method: 'POST' }),

  getErrors: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/errors${qs ? `?${qs}` : ''}`);
  },
  getErrorSummary: () => request('/errors/summary'),
  clearErrors: (olderThanDays = 30) => request(`/errors/clear?olderThanDays=${olderThanDays}`, { method: 'DELETE' }),
};
